<template>
  <HgBreadcrumb
    v-if="linksArray.length > 0"
    data-testid="breadCrumbs"
    :theme="getCurrentPlatformTheme()"
    :links="linksArray"
  />
</template>
<script setup lang="ts">
import { HgBreadcrumb, type HgBreadcrumbModel, HgSupportedLanguage } from '@smg-real-estate/domus';
import { type GeoLocation } from '@smg-real-estate/geo-libs';
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { getGeoLocationById } from '@/api/geoService';
import { getDistinguishedDisplayValue } from '@/helpers/geo/geoLocationHelper';
import {
  cityLocale,
  municipalityLocale,
  replaceGeoTagReg,
  zipLocale,
} from '@/helpers/geo/geoUrlHelper';
import { getCurrentPlatformTheme, isPlatformIs24 } from '@/helpers/platform';
import { getBasePathWithLocation } from '@/router';
import { type UrlParams } from '@/stores/types';
import useGeoStore from '@/stores/useGeoStore';
import { GeoIdLevels } from '@/types';

const { locale, t } = useI18n();
const geoStore = useGeoStore();
const { searchLocation } = storeToRefs(geoStore);
const homeLink = (): HgBreadcrumbModel => {
  return {
    name: t('common.homePage'),
    link: `${import.meta.env.VUE_PLATFORM_BASE_URL}/${locale.value}`,
  };
};
const countryLink = (): HgBreadcrumbModel => {
  return {
    name: t('common.mapTitle'),
    link: `${import.meta.env.VUE_PLATFORM_BASE_URL}${getBasePathWithLocation(locale.value as HgSupportedLanguage)}`,
  };
};
const linksArray = ref<Array<HgBreadcrumbModel>>([homeLink(), countryLink()]);
const generateLinks = () => {
  linksArray.value = [];
  linksArray.value.push(homeLink());
  linksArray.value.push(countryLink());
  if (searchLocation.value && searchLocation.value.parentPaths) {
    for (let i = searchLocation.value.parentPaths[0].length - 1; i >= 0; i--) {
      checkParentLevel(searchLocation.value.parentPaths[0][i]);
    }
    checkParentLevel(searchLocation.value.id);
  }
};

const checkParentLevel = (parentPath: string) => {
  if (
    parentPath.includes(GeoIdLevels.Canton) ||
    parentPath.includes(GeoIdLevels.Municipality) ||
    parentPath.includes(GeoIdLevels.Town)
  ) {
    getGeoUrlFromGeoId(parentPath);
  }
};

const getGeoUrlFromGeoId = async (geoId: string) => {
  const location = await getGeoLocationById(geoId);
  let parentLoc = null;
  if (location) {
    if (location.type === 'ziparea') {
      const parentGeoId = location.parentPaths?.[0].find((item) => item.indexOf('city') > -1);
      parentLoc = parentGeoId ? await getGeoLocationById(parentGeoId) : null;
    }
    const isMunicipality = location?.type === 'municipality';
    const getUrlByGeoLocation = generateUrlParams(
      locale.value as HgSupportedLanguage,
      getUrlName(location),
      getUrlName(parentLoc),
      isMunicipality,
    );
    addBreadCrumbData(getUrlByGeoLocation, location);
  }
};
const getUrlName = (location: GeoLocation | null): string => {
  const language: HgSupportedLanguage = locale.value as HgSupportedLanguage;
  if (!location) {
    return '';
  }
  if (isPlatformIs24()) {
    return location.is24UrlNames ? location.is24UrlNames[language] : '';
  }
  return location.urlNames[language];
};
const addBreadCrumbData = (getUrlByGeoLocation: UrlParams, location: GeoLocation) => {
  const locationName = getDistinguishedDisplayValue(location, locale.value as HgSupportedLanguage, {
    inclAllNames: true,
  });
  let tmpUrl = getUrlByGeoLocation.param;
  if (location.type == 'ziparea') {
    tmpUrl = tmpUrl + '?zipCity=' + getUrlByGeoLocation.zipCity;
  }
  const geoUrl = `${import.meta.env.VUE_PLATFORM_BASE_URL}${getBasePathWithLocation(locale.value as HgSupportedLanguage, tmpUrl)}`;
  linksArray.value.push({
    name: locationName,
    link: geoUrl,
  });
};
const generateUrlParams = (
  language: HgSupportedLanguage,
  geoUrlName?: string,
  parentGeoUrlName?: string,
  isMunicipality?: boolean,
): UrlParams => ({
  param: parentGeoUrlName
    ? parentGeoUrlName
    : isMunicipality
      ? geoUrlName!.replace(municipalityLocale[language], cityLocale[language])
      : (geoUrlName ?? ''),
  zipCity: parentGeoUrlName ? geoUrlName!.replace(replaceGeoTagReg(zipLocale[language]), '') : '',
});
watch(
  searchLocation,
  () => {
    generateLinks();
  },
  { immediate: true },
);
watch(locale, () => {
  generateLinks();
});
</script>
